import { useState, useEffect } from 'react';
import { Calendar, Filter, Download, RefreshCw } from 'lucide-react';
import api from '../api/client';

function formatCurrency(value) {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export default function TradeLog({ initialTrades = [] }) {
  const [trades, setTrades] = useState(initialTrades);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [filterAccount, setFilterAccount] = useState('');
  const [filterTicker, setFilterTicker] = useState('');

  const fetchTrades = async () => {
    setLoading(true);
    try {
      const response = await api.getTrades(days, filterAccount || null, filterTicker || null);
      setTrades(response.trades);
    } catch (err) {
      console.error('Error fetching trades:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrades();
  }, [days]);

  const handleFilter = () => {
    fetchTrades();
  };

  // Get unique accounts from trades
  const accounts = [...new Set(trades.map(t => t.account_id))].sort();

  // Calculate summary stats
  const totalBuys = trades.filter(t => t.action === 'BUY').reduce((sum, t) => sum + (t.total_value || t.shares * t.price), 0);
  const totalSells = trades.filter(t => t.action === 'SELL').reduce((sum, t) => sum + (t.total_value || t.shares * t.price), 0);
  const netFlow = totalBuys - totalSells;

  const exportToCsv = () => {
    const headers = ['Date', 'Account', 'Ticker', 'Action', 'Shares', 'Price', 'Total', 'Notes'];
    const rows = trades.map(t => [
      t.trade_date,
      t.account_id,
      t.ticker,
      t.action,
      t.shares,
      t.price,
      t.total_value || t.shares * t.price,
      t.notes || '',
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trades-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="stat-card bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30">
          <p className="stat-label">Total Buys</p>
          <p className="stat-value text-emerald-400">{formatCurrency(totalBuys)}</p>
        </div>
        <div className="stat-card bg-gradient-to-br from-red-500/20 to-red-600/10 border border-red-500/30">
          <p className="stat-label">Total Sells</p>
          <p className="stat-value text-red-400">{formatCurrency(totalSells)}</p>
        </div>
        <div className={`stat-card bg-gradient-to-br ${netFlow >= 0 ? 'from-sky-500/20 to-sky-600/10 border-sky-500/30' : 'from-amber-500/20 to-amber-600/10 border-amber-500/30'}`}>
          <p className="stat-label">Net Flow</p>
          <p className={`stat-value ${netFlow >= 0 ? 'text-sky-400' : 'text-amber-400'}`}>{formatCurrency(netFlow)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="min-w-[120px]"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
              <option value={0}>All time</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={filterAccount}
              onChange={(e) => setFilterAccount(e.target.value)}
              className="min-w-[150px]"
            >
              <option value="">All Accounts</option>
              {accounts.map(acc => (
                <option key={acc} value={acc}>{acc}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Filter by ticker"
              value={filterTicker}
              onChange={(e) => setFilterTicker(e.target.value.toUpperCase())}
              className="w-32"
            />
            <button onClick={handleFilter} className="btn btn-secondary">
              Apply
            </button>
          </div>
          <div className="flex-1"></div>
          <button onClick={fetchTrades} className="btn btn-ghost" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={exportToCsv} className="btn btn-secondary">
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Account</th>
                <th>Ticker</th>
                <th>Action</th>
                <th className="text-right">Shares</th>
                <th className="text-right">Price</th>
                <th className="text-right">Total</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {trades.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-8 text-slate-400">
                    No trades found for the selected period
                  </td>
                </tr>
              ) : (
                trades.map((trade) => (
                  <tr key={trade.id}>
                    <td className="text-slate-300">{trade.trade_date}</td>
                    <td className="text-slate-400">{trade.account_id}</td>
                    <td className="font-medium text-slate-200">{trade.ticker}</td>
                    <td>
                      <span className={`badge ${trade.action === 'BUY' ? 'badge-success' : 'badge-danger'}`}>
                        {trade.action}
                      </span>
                    </td>
                    <td className="text-right font-mono">{trade.shares.toFixed(3)}</td>
                    <td className="text-right font-mono">{formatCurrency(trade.price)}</td>
                    <td className="text-right font-mono font-medium">
                      {formatCurrency(trade.total_value || trade.shares * trade.price)}
                    </td>
                    <td className="text-slate-500 text-xs">{trade.source || 'manual'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-700 bg-slate-800/50 text-sm text-slate-400">
          Showing {trades.length} trade{trades.length !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
}

